/**
 * Main Zustand Store - Combines all slices
 * Clean, modular architecture using Zustand slices pattern
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { createJSONStorage, persist } from 'zustand/middleware';

// Import slices
import {
  createPipelineSlice,
  PipelineSlice,
  createNodeSlice,
  NodeSlice,
  createEdgeSlice,
  EdgeSlice,
  createTransformStateSlice,
  TransformStateSlice,
  createHistorySlice,
  HistorySlice,
  createOtherSlice,
  OtherSlice,
} from './slices';

// Re-export types for convenience
export * from './types';

// =============================================================================
// COMBINED STORE TYPE
// =============================================================================

export type Store = PipelineSlice &
  NodeSlice &
  EdgeSlice &
  TransformStateSlice &
  HistorySlice &
  OtherSlice;

// =============================================================================
// CREATE STORE
// =============================================================================

export const useStore = createWithEqualityFn<Store>()(
  persist(
    (...a) => ({
      ...createPipelineSlice(...a),
      ...createNodeSlice(...a),
      ...createEdgeSlice(...a),
      ...createTransformStateSlice(...a),
      ...createHistorySlice(...a),
      ...createOtherSlice(...a),
    }),
    {
      name: 'hop-studio-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !['undoRedoState'].includes(key))
        ),
      version: 1,
    }
  ),
  shallow
);

// =============================================================================
// CONVENIENCE HOOKS & SELECTORS
// =============================================================================

// Active pipeline ID
export const useActivePipelineId = () => useStore((state) => state.activePipelineId);

// Active pipeline selector
export const useActivePipeline = () =>
  useStore((state) => state.pipelines.find((p) => p.id === state.activePipelineId) || null);

// Active pipeline nodes
export const useActivePipelineNodes = () =>
  useStore((state) => state.pipelines.find((p) => p.id === state.activePipelineId)?.nodes || []);

// Active pipeline edges
export const useActivePipelineEdges = () =>
  useStore((state) => state.pipelines.find((p) => p.id === state.activePipelineId)?.edges || []);

// Selected node ID and double-click state
export const useSelectedNodeId = () =>
  useStore((state) => {
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    return { 
      nodeId: activePipeline?.selectedNodeId || null,
      isDoubleClicked: activePipeline?.isNodeDoubleClicked || false,
    };
  });

// Last node position
export const useLastNodePosition = () =>
  useStore((state) => {
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    return activePipeline?.lastNodePosition || { x: 0, y: 0 };
  });

// Undo/Redo state
export const useUndoRedoState = () =>
  useStore((state) => ({
    canUndo: state.canUndo(),
    canRedo: state.canRedo(),
  }));

// Sidebar width
export const useSidebarWidth = () => useStore((state) => state.sidebarWidth);

// Pipeline actions
export const usePipelineActions = () =>
  useStore((state) => ({
    addPipeline: state.addPipeline,
    setActivePipeline: state.setActivePipeline,
    renamePipeline: state.renamePipeline,
    closePipeline: state.closePipeline,
    createPipeline: state.createPipeline,
    updatePipeline: state.updatePipeline,
    deletePipeline: state.deletePipeline,
    migratePipelineId: state.migratePipelineId,
    invalidatePipelineTransformCache: state.invalidatePipelineTransformCache,
    markStructuralChange: state.markStructuralChange,
    clearStructuralChanges: state.clearStructuralChanges,
  }));

// Flow/Node/Edge actions
export const useFlowActions = () =>
  useStore((state) => ({
    setNodes: state.setNodes,
    setEdges: state.setEdges,
    onNodesChange: state.onNodesChange,
    onEdgesChange: state.onEdgesChange,
    addNode: state.addNode,
    addEdge: state.addEdge,
    updateNode: state.updateNode,
    updateEdge: state.updateEdge,
    deleteNode: state.deleteNode,
    deleteEdge: state.deleteEdge,
    updateNodeData: state.updateNodeData,
    updateEdgeData: state.updateEdgeData,
    setSelectedNodeId: state.setSelectedNodeId,
    setLastNodePosition: state.setLastNodePosition,
    clearNodeSelectionState: state.clearNodeSelectionState,
    // Undo/Redo
    undo: state.undo,
    redo: state.redo,
    canUndo: state.canUndo,
    canRedo: state.canRedo,
    saveToHistory: state.saveToHistory,
    clearPipelineHistory: state.clearPipelineHistory, // Add this for cleanup on tab close
  }));

// Markdown actions
export const useMarkdownActions = () =>
  useStore((state) => ({
    addMarkdown: state.addMarkdown,
    updateMarkdown: state.updateMarkdown,
    deleteMarkdown: state.deleteMarkdown,
  }));

// Summary actions
export const useSummaryActions = () =>
  useStore((state) => ({
    addSummary: state.addSummary,
    updateSummary: state.updateSummary,
    deleteSummary: state.deleteSummary,
  }));

// Sidebar actions
export const useSidebarActions = () =>
  useStore((state) => ({
    setSidebarWidth: state.setSidebarWidth,
  }));

// Get markdown by ID
export const useMarkdown = (id: string) =>
  useStore((state) => state.markdowns.find((m) => m.id === id));

// Get all markdowns
export const useMarkdowns = () => useStore((state) => state.markdowns);

// Get summary by ID
export const useSummary = (id: string) =>
  useStore((state) => state.summaries.find((s) => s.id === id));

// Get all summaries
export const useSummaries = () => useStore((state) => state.summaries);
